import React, { useState } from 'react';
import './ScheduleDemoForm.scss';
import Form from '../UI/Form/Form';
import { useForm } from 'react-hook-form';
import { emailPattern } from '../../util/regex/emailPattern';
import ErrorMessage from '../Footer/NewsletterSignUp/ErrorMessage/ErrorMessage';
import Select from '../UI/Select/Select';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import { HUBSPOT_FORM_CONFIG } from '../../constants/hubspotFormConfig';
import FormSuccessMessage from '../UI/Form/FormSuccessMessage/FormSuccessMessage';
import { numberEmployeesList } from './numberEmployeesList';
import { phonePattern } from '../../util/regex/phonePattern';

import { countriesList } from '../../util/countriesList';

/**
 *
 * @param {string} title
 * @param {string} subtitle
 * @param {string} formTitle
 * @param {string} ctaText
 * @param {string} successTitle
 * @param {string} successMessage
 * @param {string} disclaimer
 * @param {string} listTitle
 * @param {array} items
 * @returns {JSX.Element}
 * @constructor
 */
const ScheduleDemoForm = ({
  title,
  subtitle,
  formTitle,
  ctaText,
  successTitle,
  successMessage,
  disclaimer,
  listTitle,
  items,
}) => {
  const { register, handleSubmit, errors, trigger, formState } = useForm({ mode: 'onChange' });
  const [formComplete, setFormComplete] = useState(false);
  const [status, setStatus] = useState(null);

  const rows = [
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="firstname" className="c-form__input-label eyebrow-2">
          FIRST NAME
        </label>
        <input
          id={'firstname'}
          name={'firstname'}
          className={`body-2 color-black c-form__input ${errors['firstname'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('firstname')}
        />
        {errors['firstname'] && <ErrorMessage message={'First name is required'} />}
      </div>
      <div className="c-form__input-container">
        <label htmlFor="lastname" className="c-form__input-label eyebrow-2">
          LAST NAME
        </label>
        <input
          id={'lastname'}
          name={'lastname'}
          className={`body-2 color-black c-form__input ${errors['lastname'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('lastname')}
        />
        {errors['lastname'] && <ErrorMessage message={'Last name is required'} />}
      </div>
    </div>,
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="email" className="c-form__input-label eyebrow-2">
          EMAIL
        </label>
        <input
          id={'email'}
          name={'email'}
          className={`body-2 color-black c-form__input ${errors['email'] ? 'error' : ''}`}
          type="email"
          ref={register({
            required: true,
            pattern: emailPattern,
          })}
          onBlur={() => trigger('email')}
        />
        {errors['email'] && <ErrorMessage message={'Please enter a valid email address'} />}
      </div>
      <div className="c-form__input-container">
        <label htmlFor="phone" className="c-form__input-label eyebrow-2">
          PHONE NUMBER
        </label>
        <input
          id={'phone'}
          name={'phone'}
          className={`body-2 color-black c-form__input ${errors['phone'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
            pattern: phonePattern,
          })}
          onBlur={() => trigger('phone')}
        />
        {errors['phone'] && <ErrorMessage message={'Please enter a valid phone number'} />}
      </div>
    </div>,
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="state" className="c-form__input-label eyebrow-2">
          STATE
        </label>
        <input
          id={'state'}
          name={'state'}
          className={`body-2 color-black c-form__input ${errors['state'] ? 'error' : ''}`}
          type="state"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('state')}
        />
        {errors['state'] && <ErrorMessage message={'State is required'} />}
      </div>
      <div className="c-form__input-container select-element">
        <Select
          fieldRef={register({ required: true })}
          label={'COUNTRY'}
          type={'translucent'}
          data={countriesList}
          id={'country'}
          fieldName={'country'}
          onChange={() => trigger('country')}
        />
        {errors['country'] && <ErrorMessage message={'Country is required'} />}
      </div>
    </div>,
    <div className="c-form__row">
      <div className="c-form__input-container">
        <label htmlFor="company" className="c-form__input-label eyebrow-2">
          COMPANY NAME
        </label>
        <input
          id={'company'}
          name={'company'}
          className={`body-2 color-black c-form__input ${errors['company'] ? 'error' : ''}`}
          type="text"
          ref={register({
            required: true,
          })}
          onBlur={() => trigger('company')}
        />
        {errors['company'] && <ErrorMessage message={'Company name is required'} />}
      </div>
      <div className="c-form__input-container select-element">
        <Select
          fieldRef={register({ required: true })}
          label={'NUMBER OF EMPLOYEES'}
          type={'translucent'}
          data={numberEmployeesList}
          id={'numemployees'}
          fieldName={'numemployees'}
          onChange={() => trigger('numemployees')}
        />
        {errors['numemployees'] && <ErrorMessage message={'Number of employees is required'} />}
      </div>
    </div>,
    <input ref={register} name="demo_time" type="hidden" value="" />,
    <input ref={register} name="lifecyclestage" type="hidden" value="marketingqualifiedlead" />,
    <input ref={register} name="sales_request" type="hidden" value="Demo Request" />,
    <div className="c-form__row">
      <CTAButton
        type={'primary'}
        name={'submit'}
        text={ctaText}
        additionalClasses={'c-form__submit cta'}
        isButton={true}
        buttonType={'submit'}
        disabled={!formState.isValid}
      />
    </div>,
  ];

  return (
    <div className="c-schedule-demo-form background-color-blue">
      <div className="c-schedule-demo-form__inner">
        <div className="c-schedule-demo-form__header">
          <div className="c-schedule-demo-form__title h4-right-grotesk color-white">
            <h4>{title}</h4>
          </div>
          <div className="c-schedule-demo-form__subtitle body-1 color-white">
            <p>{subtitle}</p>
          </div>
        </div>
        <div className="c-schedule-demo-form__form-text-container">
          <div className="c-schedule-demo-form__form-container background-color-white">
            {!formComplete && (
              <>
                <div className="c-schedule-demo-form__form-title h3-roobert color-black">
                  <h3>{formTitle}</h3>
                </div>
                <Form
                  handleSubmit={handleSubmit}
                  guid={HUBSPOT_FORM_CONFIG.DEMO.GUID}
                  ctaText={ctaText}
                  rows={rows}
                  setFormComplete={setFormComplete}
                  setStatus={setStatus}
                />
                <div
                  className="c-schedule-demo-form__disclaimer body-3 color-black"
                  dangerouslySetInnerHTML={{ __html: disclaimer }}
                ></div>
              </>
            )}
            {formComplete && <FormSuccessMessage title={successTitle} message={successMessage} />}
          </div>
          <div className="c-schedule-demo-form__list-container">
            <div className="c-schedule-demo-form__list-title color-white h3-roobert">
              <h3>{listTitle}</h3>
            </div>
            <ul className="c-schedule-demo-form__list color-white">
              {items.map((item) => {
                return (
                  <li
                    key={item.text}
                    className="c-schedule-demo-form__list-item color-white body-1"
                  >
                    {item.text}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDemoForm;
